/**
 * Canonical names plus the strings already on GA4, GTM, and Vercel.
 * A tap emits the canonical name and its historical names together.
 */

export const CATALOG = {
  viewItem: 'view_item',
  addToCart: 'add_to_cart',
  addToCartFailed: 'add_to_cart_failed',
  removeFromCart: 'remove_from_cart',
  viewCart: 'view_cart',
  beginCheckout: 'begin_checkout',
  selectItem: 'select_item',
  selectContent: 'select_content',
  generateLead: 'generate_lead',
  addPaymentInfo: 'add_payment_info',
  paymentFailed: 'payment_failed',
} as const;

export type CanonicalEvent = (typeof CATALOG)[keyof typeof CATALOG];

const ALIAS_TO_CANONICAL: Record<string, string> = {
  'Bottom bar addto cart - CLICK': CATALOG.addToCart,
  'Rare Bottom bar addto cart - CLICK': CATALOG.addToCart,
  'ring-air-checkout': CATALOG.addToCart,
  'ring-pro-checkout': CATALOG.addToCart,
  'rare-checkout': CATALOG.addToCart,
  'CLICK: Product Selection, Add to Cart': CATALOG.addToCart,
  'Bottom bar continue - CLICK': CATALOG.selectContent,
  ringBuyCtaClick: CATALOG.selectContent,
  ring_buy_cta_click: CATALOG.selectContent,
  applepay_button_clicked: CATALOG.addPaymentInfo,
  applepay_payment_succeeded: CATALOG.addPaymentInfo,
  applepay_payment_failed: CATALOG.paymentFailed,
  applepay_sheet_authorized: CATALOG.addPaymentInfo,
};

/** Historical click names for a canonical add-to-cart, by product and placement. */
function addToCartLegacy(product: string, placement: string): string[] {
  if (placement === 'bottom_bar' && product === 'rare') {
    return ['Rare Bottom bar addto cart - CLICK', 'rare-checkout'];
  }
  if (placement === 'bottom_bar' && product === 'ring_pro') {
    return ['Bottom bar addto cart - CLICK', 'ring-pro-checkout'];
  }
  if (placement === 'bottom_bar' && (product === 'ring_air' || product === 'diesel')) {
    return ['Bottom bar addto cart - CLICK', 'ring-air-checkout'];
  }
  if (placement === 'summary' && product) {
    return ['CLICK: Product Selection, Add to Cart'];
  }
  return [];
}

export function canonicalName(name: string): string | null {
  return ALIAS_TO_CANONICAL[name] ?? null;
}

/**
 * Names that leave the page for one tap. The caller's name is always first.
 * An old name also emits its canonical sibling. A canonical add-to-cart also
 * emits the historical click strings for that product and placement.
 */
export function wireNames(
  name: string,
  props: Record<string, unknown> | undefined,
  legacy?: string | readonly string[],
): string[] {
  const names = [name];
  const extra = legacy == null ? [] : typeof legacy === 'string' ? [legacy] : [...legacy];
  for (const item of extra) {
    if (item && !names.includes(item)) names.push(item);
  }
  const canonical = ALIAS_TO_CANONICAL[name];
  if (canonical && !names.includes(canonical)) names.push(canonical);
  if (name === CATALOG.addToCart) {
    const product = typeof props?.product === 'string' ? props.product : '';
    const placement = typeof props?.placement === 'string' ? props.placement : '';
    for (const item of addToCartLegacy(product, placement)) {
      if (!names.includes(item)) names.push(item);
    }
  }
  return names;
}

export function pixelNameFor(names: readonly string[]): string | null {
  for (const name of names) {
    if (
      name === CATALOG.viewItem ||
      name === CATALOG.addToCart ||
      name === CATALOG.beginCheckout ||
      name === CATALOG.generateLead
    ) {
      return name;
    }
  }
  return null;
}
